import React, { useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderItem from './FolderItem';
import FileItem from './FileItem';
import ExtractedImagesModal from './ExtractedImagesModal';
import MarkdownPreviewModal from './MarkdownPreviewModal';
import CreateNewButton from './CreateNewButton';
import { getRightworkAPI } from '../requests/utilities/requests';
import { postRightworkAPI } from '../requests/utilities/requests';
import {
  API_get_documents,
  API_create_folder,
  API_rename_folder,
  API_move_folder,
  API_delete_folder,
  API_rename_file,
  API_move_file,
  API_post_file_upload_with_eagerly_generated_presigned_url,
  API_post_bulk_file_upload_with_eagerly_generated_presigned_urls,
  API_get_file_upload,
  API_notify_file_upload_complete,
  API_delete_file_upload,
  API_get_trash,
  API_toggle_folder_assistant_searchable,
  API_set_folder_permission_level,
} from '../../../constants';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { getDefaultAxiosConfig } from '../requests/utilities/requests';
import LoadSpinner from '../../../utilities/LoadSpinner';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AppsIcon from '@mui/icons-material/Apps';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrashView from './TrashView';

function DocumentsDisplay(props) {
  const [currentFolder, setCurrentFolder] = useState(null);
  // File whose extracted-photos gallery is open (null = closed).
  const [photosFile, setPhotosFile] = useState(null);
  // File open in the markdown preview modal (null = closed).
  const [previewFile, setPreviewFile] = useState(null);
  const [documents, setDocuments] = useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const [isFolderDragging, setIsFolderDragging] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashCount, setTrashCount] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [viewMode, setViewMode] = useState('grid');
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = React.useRef(0);
  const breadcrumbScrollRef = React.useRef(null);

  // Check if user can edit documents (create, rename, move, delete)
  // Only rightwork_internal or users with documents_editor special permission
  const isDocumentsEditor = props.permissionOverrides?.includes("documents_editor");
  const canEditDocuments = (
    props.permissionRole === "rightwork_internal" ||
    isDocumentsEditor
  );

  // Setting a folder's permission tier is admin-only, matching the backend's
  // admin-gated set_folder_permission_level. A documents_editor can edit
  // documents but cannot change who can access them.
  const canSetPermissions = (
    props.permissionRole === "company_admin" ||
    props.permissionRole === "rightwork_internal"
  );

  const fetchDocuments = async (showLoading) => {
    setIsLoading(showLoading);
    let api_params = {
      company_id: props.homeCompany,
      store_id: props.homeStore,
    };

    try {
      const res = await getRightworkAPI(API_get_documents, api_params);
      if (res.status === 200) {
        const data = res.data?.redux?.payload?.documents;
        setDocuments(data);

        // Only update the current folder if it is null
        if (!currentFolder) {
          const homeFolder = Object.values(data).find(
            (item) => item.type === "folder" && item.parentUuid === null
          );

          if (homeFolder) {
            setCurrentFolder(homeFolder.uuid);
          }
        }

        setIsLoading(false);
      }
    } catch (error) {
      toast.error("Failed to fetch documents");
      setIsLoading(false);
    }
  };



  React.useEffect(() => {
    fetchDocuments(true);
  }, [props.homeCompany]);

  const fetchTrashCount = async () => {
    if (!canEditDocuments) return;
    try {
      const res = await getRightworkAPI(API_get_trash, {
        company_id: props.homeCompany,
        store_id: props.homeStore,
      });
      if (res.status === 200) {
        const items = res.data?.redux?.payload?.trash_items || [];
        setTrashCount(items.length);
      }
    } catch (error) {
      // Silent fail — trash count is non-critical
    }
  };

  React.useEffect(() => {
    fetchTrashCount();
  }, [props.homeCompany, props.homeStore]);

  // The breadcrumb strip scrolls horizontally (LTR), so a deep path leaves the
  // current/rightmost crumb off-screen behind the action controls. Pin the
  // scroll to the far right whenever the path changes so the active folder
  // stays visible after navigation.
  React.useEffect(() => {
    const el = breadcrumbScrollRef.current;
    if (el) {
      el.scrollLeft = el.scrollWidth;
    }
  }, [currentFolder, documents, isSearching]);


  // ----------- POST APIs ----------- //

  const create_folder = async (name, parent_folder_uuid) => {
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      name: name,
      parent_folder_uuid: parent_folder_uuid
    };

    try {
      toast.loading("Creating Folder...", { autoClose: false });

      const res = await postRightworkAPI(API_create_folder, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Folder Created Successfully", { autoClose: 3000 });

        fetchDocuments(false)
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };


  const rename_folder = async (folder_uuid, new_folder_name) => {
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      folder_uuid: folder_uuid,
      new_folder_name: new_folder_name
    };

    try {
      toast.loading("Renaming Folder...", { autoClose: false });

      const res = await postRightworkAPI(API_rename_folder, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Folder Renamed Successfully", { autoClose: 3000 });

        fetchDocuments(false)
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  const move_folder = async (moving_folder_uuid, new_parent_folder_uuid) => {
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      moving_folder_uuid: moving_folder_uuid,
      new_parent_folder_uuid
    };

    // Store the original parent UUID to revert if needed
    const originalParentUuid = documents[moving_folder_uuid].parentUuid;

    try {
      // Optimistic state update
      setDocuments(prevDocuments => {
        const updatedDocuments = { ...prevDocuments };
        if (updatedDocuments[moving_folder_uuid]) {
          updatedDocuments[moving_folder_uuid] = {
            ...updatedDocuments[moving_folder_uuid],
            parentUuid: new_parent_folder_uuid
          };
        }
        return updatedDocuments;
      });

      toast.loading("Moving Folder...", { autoClose: false });

      const res = await postRightworkAPI(API_move_folder, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Folder Moved Successfully", { autoClose: 3000 });
      }
    } catch (error) {
      // Revert state update if API call fails
      setDocuments(prevDocuments => {
        const revertedDocuments = { ...prevDocuments };
        if (revertedDocuments[moving_folder_uuid]) {
          revertedDocuments[moving_folder_uuid] = {
            ...revertedDocuments[moving_folder_uuid],
            parentUuid: originalParentUuid
          };
        }
        return revertedDocuments;
      });

      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  const delete_folder = async (folder_uuid) => {
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      folder_uuid: folder_uuid,
    };

    try {
      toast.loading("Deleting Folder...", { autoClose: false });

      const res = await postRightworkAPI(API_delete_folder, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("Folder Deleted Successfully", { autoClose: 3000 });

        fetchDocuments(false);
        fetchTrashCount();
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  async function upload_file(file, file_upload_folder_uuid) {
    const formData = new FormData();
    formData.append("company_id", props.homeCompany);
    formData.append("file_name", file.name);
    formData.append("type", "file");  // NOTE: Always "file" here for the filesystem
    formData.append("file_upload_folder_uuid", file_upload_folder_uuid)

    // [1] Get presigned URL
    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_file_upload_with_eagerly_generated_presigned_url,
      formData,
      {
        headers: {
          ...axiosConfig.headers,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const fileUUID = res?.data?.redux?.payload?.uuid;
    const presignedUrl = res?.data?.redux?.payload?.presigned_url;

    // [2] Upload file
    await axios.put(presignedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
    });

    // [2.5] Trigger pgvector indexing (fire-and-forget)
    postRightworkAPI(API_notify_file_upload_complete, {
      file_upload_uuid: fileUUID,
      company_id: props.homeCompany,
    }).catch(() => {});

    // [3] Get file URL
    let api_params = {
      file_upload_uuid: fileUUID,
    };
    const resFile = await getRightworkAPI(API_get_file_upload, api_params);
    const resFilePayload = resFile?.data?.redux?.payload;

    fetchDocuments(false)

    // [4] Weeee
    return resFilePayload;
  }

  async function upload_folder(fileList, file_upload_folder_uuid) {
    const filesPayload = fileList.map((f) => ({
      path: f.webkitRelativePath || f.name,
      name: f.name,
    }));

    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.post(
      API_post_bulk_file_upload_with_eagerly_generated_presigned_urls,
      {
        company_id: props.homeCompany,
        parent_folder_uuid: file_upload_folder_uuid,
        files: filesPayload,
      },
      axiosConfig
    );

    const results = res?.data?.redux?.payload?.files || [];
    for (const result of results) {
      const targetFile = fileList.find(
        (f) => (f.webkitRelativePath || f.name) === result.path
      );
      if (!targetFile || result.error) continue;

      await axios.put(result.presigned_url, targetFile, {
        headers: { "Content-Type": targetFile.type },
      });

      // Trigger pgvector indexing (fire-and-forget)
      postRightworkAPI(API_notify_file_upload_complete, {
        file_upload_uuid: result.uuid,
        company_id: props.homeCompany,
      }).catch(() => {});

      const resFile = await getRightworkAPI(API_get_file_upload, {
        file_upload_uuid: result.uuid,
      });
      void resFile;
    }

    fetchDocuments(false);
  }


  const rename_file = async (file_uuid, new_file_name) => {
    // Prepare data to send
    const requestData = {
      company_id: props.homeCompany,
      file_uuid: file_uuid,
      new_file_name: new_file_name
    };

    try {
      toast.loading("Renaming File...", { autoClose: false });

      const res = await postRightworkAPI(API_rename_file, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("File Renamed Successfully", { autoClose: 3000 });

        fetchDocuments(false)
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  const move_file = async (moving_file_uuid, new_parent_folder_uuid) => {
    const requestData = {
      company_id: props.homeCompany,
      moving_file_uuid: moving_file_uuid,
      new_parent_folder_uuid: new_parent_folder_uuid
    };

    // Store the original parent UUID to revert if needed
    const originalParentUuid = documents[moving_file_uuid].parentUuid;

    try {
      // Optimistic state update
      setDocuments(prevDocuments => {
        const updatedDocuments = { ...prevDocuments };
        if (updatedDocuments[moving_file_uuid]) {
          updatedDocuments[moving_file_uuid] = {
            ...updatedDocuments[moving_file_uuid],
            parentUuid: new_parent_folder_uuid
          };
        }
        return updatedDocuments;
      });

      toast.loading("Moving File...", { autoClose: false });

      const res = await postRightworkAPI(API_move_file, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("File Moved Successfully", { autoClose: 3000 });

        fetchDocuments(false);
      }
    } catch (error) {
      // Revert state update if API call fails
      setDocuments(prevDocuments => {
        const revertedDocuments = { ...prevDocuments };
        if (revertedDocuments[moving_file_uuid]) {
          revertedDocuments[moving_file_uuid] = {
            ...revertedDocuments[moving_file_uuid],
            parentUuid: originalParentUuid
          };
        }
        return revertedDocuments;
      });

      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  const delete_file = async (file_upload_uuid) => {
    // Prepare data to send
    const requestData = {
      file_upload_uuid: file_upload_uuid,
      store_id: props.homeStore
    };

    try {
      toast.loading("Deleting File...", { autoClose: false });

      const res = await postRightworkAPI(API_delete_file_upload, requestData);

      if (res.status === 200) {
        toast.dismiss();
        toast.success("File Deleted Successfully", { autoClose: 3000 });

        fetchDocuments(false);
        fetchTrashCount();
      }
    } catch (error) {
      toast.dismiss();

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Request Failed", { autoClose: 3000 });
      }

      console.error("Error:", error);
    }
  };

  const getDescendantFolderUuids = (folderUuid) => {
    const visited = new Set([folderUuid]);
    const result = [folderUuid];
    const queue = [folderUuid];
    while (queue.length > 0) {
      const current = queue.shift();
      Object.values(documents).forEach(item => {
        if (item.type === 'folder' && item.parentUuid === current && !visited.has(item.uuid)) {
          visited.add(item.uuid);
          result.push(item.uuid);
          queue.push(item.uuid);
        }
      });
    }
    return result;
  };

  const toggle_folder_assistant_searchable = async (folder_uuid, assistant_searchable) => {
    const affectedUuids = getDescendantFolderUuids(folder_uuid);

    // Snapshot original values for rollback
    const previousValues = {};
    for (const uuid of affectedUuids) {
      if (documents[uuid]) {
        previousValues[uuid] = documents[uuid].assistantSearchable;
      }
    }

    // Optimistic UI update — propagate to all descendants
    setDocuments(prevDocuments => {
      const updatedDocuments = { ...prevDocuments };
      for (const uuid of affectedUuids) {
        if (updatedDocuments[uuid]) {
          updatedDocuments[uuid] = {
            ...updatedDocuments[uuid],
            assistantSearchable: assistant_searchable,
          };
        }
      }
      return updatedDocuments;
    });

    try {
      await postRightworkAPI(API_toggle_folder_assistant_searchable, {
        company_id: props.homeCompany,
        folder_uuid: folder_uuid,
        assistant_searchable: assistant_searchable,
      });

      toast.success(
        assistant_searchable
          ? "AI search enabled — indexing will begin shortly"
          : "AI search disabled",
        { autoClose: 3000 }
      );
    } catch (error) {
      // Revert on failure — restore original values
      setDocuments(prevDocuments => {
        const revertedDocuments = { ...prevDocuments };
        for (const uuid of affectedUuids) {
          if (revertedDocuments[uuid] && uuid in previousValues) {
            revertedDocuments[uuid] = {
              ...revertedDocuments[uuid],
              assistantSearchable: previousValues[uuid],
            };
          }
        }
        return revertedDocuments;
      });

      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to update AI search setting", { autoClose: 3000 });
      }
      console.error("Error:", error);
    }
  };

  const set_folder_permission_level = async (folder_uuid, min_permission_level) => {
    // Optimistically update this folder's OWN tier for instant radio feedback.
    // Changing a parent also changes the EFFECTIVE tier of every descendant
    // (resolved server-side), which a single-folder optimistic update can't
    // express — so on success we re-fetch to refresh descendant badges. Fall
    // back to the default tier (not undefined) so rollback can't write undefined.
    const previousValue = documents[folder_uuid]?.minPermissionLevel || 'employee';

    setDocuments(prevDocuments => {
      if (!prevDocuments[folder_uuid]) return prevDocuments;
      return {
        ...prevDocuments,
        [folder_uuid]: {
          ...prevDocuments[folder_uuid],
          minPermissionLevel: min_permission_level,
        },
      };
    });

    try {
      await postRightworkAPI(API_set_folder_permission_level, {
        company_id: props.homeCompany,
        folder_uuid: folder_uuid,
        min_permission_level: min_permission_level,
      });
      toast.success("Folder access updated", { autoClose: 3000 });
      // Refresh so descendants' effective (inherited) tiers re-resolve.
      fetchDocuments(false);
    } catch (error) {
      // Revert on failure
      setDocuments(prevDocuments => {
        if (!prevDocuments[folder_uuid]) return prevDocuments;
        return {
          ...prevDocuments,
          [folder_uuid]: {
            ...prevDocuments[folder_uuid],
            minPermissionLevel: previousValue,
          },
        };
      });
      if (error.response?.data?.system?.message) {
        toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to update folder access", { autoClose: 3000 });
      }
      console.error("Error:", error);
    }
  };

  const requestDeleteFile = (file_uuid) => {
    const file = documents[file_uuid];
    setDeleteTarget({
      uuid: file_uuid,
      type: 'file',
      name: file?.displayName || file?.name || 'this file',
      hasContents: false,
    });
    setDeleteModalOpen(true);
  };

  const requestDeleteFolder = (folder_uuid) => {
    const folder = documents[folder_uuid];
    const hasContents = Object.values(documents).some(
      (item) => item.parentUuid === folder_uuid
    );
    setDeleteTarget({
      uuid: folder_uuid,
      type: 'folder',
      name: folder?.displayName || folder?.name || 'this folder',
      hasContents: hasContents,
    });
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (deleteTarget.type === 'file') {
      delete_file(deleteTarget.uuid);
    } else {
      delete_folder(deleteTarget.uuid);
    }
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  // --- External drag-and-drop upload --- //

  const readEntryAsFile = (fileEntry) => {
    return new Promise((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
  };

  const readDirectoryEntries = (dirReader) => {
    return new Promise((resolve, reject) => {
      dirReader.readEntries(resolve, reject);
    });
  };

  const traverseDirectory = async (entry, path = '') => {
    const files = [];
    if (entry.isFile) {
      const file = await readEntryAsFile(entry);
      // Attach the relative path so upload_folder can use it
      Object.defineProperty(file, 'webkitRelativePath', {
        value: path + file.name,
        writable: false,
      });
      files.push(file);
    } else if (entry.isDirectory) {
      const dirReader = entry.createReader();
      let entries = [];
      // readEntries may not return all entries at once, need to call until empty
      let batch;
      do {
        batch = await readDirectoryEntries(dirReader);
        entries = entries.concat(Array.from(batch));
      } while (batch.length > 0);

      for (const child of entries) {
        const childFiles = await traverseDirectory(child, path + entry.name + '/');
        files.push(...childFiles);
      }
    }
    return files;
  };

  const handleExternalDrop = async (dataTransfer, targetFolderUuid = currentFolder) => {
    if (!canEditDocuments) {
      toast.error("You don't have permission to upload files");
      return;
    }

    // Use webkitGetAsEntry to detect folders vs files
    const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];
    const entries = items
      .map(item => item.webkitGetAsEntry ? item.webkitGetAsEntry() : null)
      .filter(Boolean);

    // Check if any of the dropped items are directories
    const hasDirectories = entries.some(entry => entry.isDirectory);

    if (hasDirectories) {
      // Handle folder upload — traverse directories to get all files with paths
      toast.loading("Reading folder contents...", { autoClose: false });

      let allFiles = [];
      for (const entry of entries) {
        const files = await traverseDirectory(entry);
        allFiles = allFiles.concat(files);
      }

      toast.dismiss();

      if (allFiles.length === 0) {
        toast.error("No files found in the dropped folder", { autoClose: 3000 });
        return;
      }

      toast.loading(`Uploading ${allFiles.length} file${allFiles.length !== 1 ? 's' : ''}...`, { autoClose: false });

      try {
        await upload_folder(allFiles, targetFolderUuid);
        toast.dismiss();
        toast.success(`Folder uploaded successfully`, { autoClose: 3000 });
      } catch (error) {
        toast.dismiss();
        toast.error("Failed to upload folder", { autoClose: 3000 });
        console.error("Folder upload error:", error);
      }
    } else {
      // Handle individual file uploads
      const files = Array.from(dataTransfer.files);
      if (files.length === 0) return;

      toast.loading(`Uploading ${files.length} file${files.length !== 1 ? 's' : ''}...`, { autoClose: false });

      let successCount = 0;
      for (let i = 0; i < files.length; i++) {
        try {
          await upload_file(files[i], targetFolderUuid);
          successCount++;
        } catch (error) {
          console.error(`Failed to upload ${files[i].name}:`, error);
        }
      }

      toast.dismiss();

      if (successCount === files.length) {
        toast.success(`${successCount} file${successCount !== 1 ? 's' : ''} uploaded`, { autoClose: 3000 });
      } else if (successCount > 0) {
        toast.warning(`${successCount} of ${files.length} files uploaded`, { autoClose: 3000 });
      } else {
        toast.error("Upload failed", { autoClose: 3000 });
      }
    }
  };

  const isExternalDrag = (e) => {
    return e.dataTransfer.types.includes('Files');
  };

  const handleContainerDragEnter = (e) => {
    e.preventDefault();
    if (!canEditDocuments || showTrash || isFileDragging || isFolderDragging) return;
    if (!isExternalDrag(e)) return;
    dragCounter.current++;
    if (dragCounter.current === 1) {
      setIsDragOver(true);
    }
  };

  const handleContainerDragOver = (e) => {
    if (!canEditDocuments || showTrash || isFileDragging || isFolderDragging) return;
    if (!isExternalDrag(e)) return;
    e.preventDefault();
  };

  const handleContainerDragLeave = (e) => {
    if (!canEditDocuments || showTrash || isFileDragging || isFolderDragging) return;
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleContainerDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragOver(false);
    if (!canEditDocuments || showTrash || isFileDragging || isFolderDragging) return;
    if (!isExternalDrag(e)) return;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      handleExternalDrop(e.dataTransfer);
    }
  };

  const searchDocuments = (query) => {
    if (!query.trim()) {
      setIsSearching(false);
      return [];
    }

    const searchTerms = query.toLowerCase().split(' ');
    const allItems = Object.values(documents);

    return allItems.filter(item => {
      const itemName = item.name.toLowerCase();
      return searchTerms.every(term => itemName.includes(term));
    });
  };

  const parseFileSize = (sizeStr) => {
    if (!sizeStr) return 0;
    const units = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 };
    const match = sizeStr.match(/([\d.]+)\s*(B|KB|MB|GB)/i);
    if (!match) return 0;
    return parseFloat(match[1]) * (units[match[2].toUpperCase()] || 1);
  };

  const handleSortClick = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const SortArrow = ({ column }) => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon style={{ fontSize: 12 }} />
      : <ArrowDownwardIcon style={{ fontSize: 12 }} />;
  };

  const getItemCount = (folderUuid) => {
    return Object.values(documents).filter(item => item.parentUuid === folderUuid).length;
  };

  const getSortableSize = (item) => {
    if (item.type === 'folder') return getItemCount(item.uuid);
    return parseFileSize(item.fileSize);
  };

  const sortItems = (items) => {
    return [...items].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.displayName || a.name).localeCompare(b.displayName || b.name);
          break;
        case 'date':
          comparison = (new Date(a.created_at || 0)).getTime() - (new Date(b.created_at || 0)).getTime();
          break;
        case 'size':
          comparison = getSortableSize(a) - getSortableSize(b);
          break;
        case 'type':
          comparison = (a.extension || '').localeCompare(b.extension || '');
          break;
        default:
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const getCurrentItems = () => {
    const items = Object.values(documents);
    return {
      folders: sortItems(items.filter(item =>
        item?.type === 'folder' &&
        item.parentUuid === currentFolder
      )),
      files: sortItems(items.filter(item =>
        item?.type === 'file' &&
        item.parentUuid === currentFolder
      ))
    };
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsSearching(!!query);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
  };


  const getBreadcrumbs = () => {
    const breadcrumbs = [];

    if (currentFolder) {
      let current = documents[currentFolder];
      while (current) {
        breadcrumbs.unshift(current);
        current = current.parentUuid ? documents[current.parentUuid] : null;
      }
    }

    // Add home folder if it exists and isn't already included
    const homeFolder = Object.values(documents).find(
      item => item.type === "folder" && item.parentUuid === null
    );
    if (homeFolder && !breadcrumbs.find(item => item.uuid === homeFolder.uuid)) {
      breadcrumbs.unshift(homeFolder);
    }

    return breadcrumbs;
  };

  // Office formats the browser can't render inline (they otherwise download on
  // click). We route these through a viewer so they preview instead.
  //
  // Spreadsheets (xls/xlsx) are intentionally excluded: the Google viewer is
  // unreliable for them (frequent "Couldn't preview file" errors), so a clean
  // download beats a flaky preview. They fall through to a normal download.
  // A future client-side renderer (e.g. SheetJS) could preview them in-browser.
  const OFFICE_PREVIEW_EXTENSIONS = ["doc", "docx", "ppt", "pptx"];

  // TODO(SOC2 / tech debt): Office files are previewed via Google's public Docs
  // Viewer, which fetches the presigned S3 URL on Google's servers — this sends
  // company-owned file contents to a third party with no DPA. Acceptable as an
  // interim UX win only; replace with a DPA-backed or self-hosted renderer
  // before treating it as compliant.
  //
  // NOTE: no `embedded=true` here — that flag is for <iframe> embeds and strips
  // the viewer chrome (incl. the download button). We open in a full tab, so we
  // want the full Drive viewer page that keeps download/print controls.
  const buildOfficeViewerUrl = (fileUrl) =>
    `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}`;

  const handleFileClick = async (fileUUID) => {
    // Markdown / plain text opens the in-app preview — a .md download
    // is a dead end for most managers. Everything else keeps the
    // presigned-URL open (office docs via the viewer, images/PDFs
    // natively in the browser).
    const ext = (documents[fileUUID]?.extension || "").toLowerCase();
    if (ext === "md" || ext === "txt") {
      setPreviewFile(documents[fileUUID]);
      return;
    }
    try {
      const res = await getRightworkAPI(API_get_file_upload, {
        file_upload_uuid: fileUUID,
      });
      const freshUrl = res?.data?.redux?.payload?.presigned_url;
      if (freshUrl) {
        const extension = (documents[fileUUID]?.extension || "").toLowerCase();
        const urlToOpen = OFFICE_PREVIEW_EXTENSIONS.includes(extension)
          ? buildOfficeViewerUrl(freshUrl)
          : freshUrl;
        window.open(urlToOpen, "_blank");
      } else {
        toast.error("Failed to open file", { autoClose: 3000 });
      }
    } catch (error) {
      toast.error("Failed to open file", { autoClose: 3000 });
      console.error("Error fetching file URL:", error);
    }
  };

  const handleFolderClick = (folderUuid) => {
    setSearchQuery('')
    // Update current folder
    setCurrentFolder(folderUuid);

    // If no documents are loaded yet, fetch them
    if (Object.keys(documents).length === 0) {
      fetchDocuments(true);
    }
  };

  const handleBreadcrumbClick = (index) => {
    const breadcrumbs = getBreadcrumbs();
    if (index === 0) {
      // Clicking home folder
      const homeFolder = breadcrumbs[0];
      setCurrentFolder(homeFolder.uuid);
    } else {
      // Clicking any other folder in breadcrumb
      setCurrentFolder(breadcrumbs[index].uuid);
    }
  };


  if (isLoading)
    return (
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <LoadSpinner />
      </div>
    );

  const items = isSearching ?
    (() => {
      const searchResults = searchDocuments(searchQuery);
      return {
        folders: sortItems(searchResults.filter(item => item.type === 'folder')),
        files: sortItems(searchResults.filter(item => item.type === 'file'))
      };
    })() :
    getCurrentItems();


  return (
      <div
        className="w-full bg-white relative"
        onDragEnter={handleContainerDragEnter}
        onDragOver={handleContainerDragOver}
        onDragLeave={handleContainerDragLeave}
        onDrop={handleContainerDrop}
      >
        {isDragOver && (
          <div
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999 }}
            className="flex items-center justify-center bg-violet-50 bg-opacity-90"
          >
            <div className="border-2 border-dashed border-violet-400 rounded-lg p-12 flex flex-col items-center w-full h-full justify-center">
              <CloudUploadIcon style={{ fontSize: 48 }} className="text-violet-400 mb-3" />
              <p className="text-violet-700 text-lg font-medium">Drop files or folders to upload</p>
              <p className="text-violet-400 text-sm mt-1">Items will be added to the current folder</p>
            </div>
          </div>
        )}

        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {showTrash ? (
              <div className="flex items-center">
                <button
                  onClick={() => setShowTrash(false)}
                  className="text-violet-700 hover:text-violet-800 px-2 py-1 rounded hover:bg-gray-100"
                >
                  Company Files
                </button>
                <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                <span className="px-2 py-1 text-slate-900">Trash</span>
              </div>
            ) : (
              <>
                {!isSearching && (
                  // Deep paths scroll sideways instead of wrapping to a second
                  // line. Every crumb stays a real, full breadcrumb so its
                  // drag-to-move drop target keeps working.
                  <div ref={breadcrumbScrollRef} className="flex items-center min-w-0 flex-1 overflow-x-auto mr-4">
                    {getBreadcrumbs().map((item, index, arr) => {
                      // Match the Training Overview breadcrumb's basic tokens:
                      // muted slate ancestors, a bold slate-900 current crumb,
                      // and a slate-300 chevron. The drag-to-move drop target +
                      // gray hover stay so every crumb still accepts a drop.
                      const isLast = index === arr.length - 1;
                      return (
                        <React.Fragment key={index}>
                          {index > 0 && (
                            <ChevronRightIcon
                              className="flex-shrink-0 text-slate-300"
                              style={{ fontSize: "18px" }}
                            />
                          )}
                          <button
                            onDragOver={(e) => {
                              e.preventDefault(); // Allow drop
                              e.currentTarget.classList.add('bg-violet-50', 'border-2', 'border-violet-700', 'px-20');
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.classList.remove('bg-violet-50', 'border-2', 'border-violet-700', 'px-20');
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove('bg-violet-50', 'border-2', 'border-violet-700', 'px-20');
                              try {
                                // Parse the dropped item from the dataTransfer
                                const droppedItem = JSON.parse(e.dataTransfer.getData('text/plain'));

                                // Check if it's a file or a folder
                                if (droppedItem.type === 'file') {
                                  move_file(droppedItem.uuid, item.uuid);
                                } else if (droppedItem.type === 'folder') {
                                  move_folder(droppedItem.uuid, item.uuid);
                                }
                              } catch (error) {
                                console.error("Error moving item:", error);
                              }
                            }}
                            onClick={() => handleBreadcrumbClick(index)}
                            title={item.displayName || item.name}
                            className={`flex-shrink-0 whitespace-nowrap px-2 py-1 rounded text-sm hover:bg-gray-100 ${
                              isLast
                                ? "font-semibold text-slate-900"
                                : "font-medium text-slate-500 hover:text-violet-700"
                            }`}
                          >
                            {item.displayName || item.name}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center space-x-3 flex-shrink-0">
                  {props.onOpenAssistant && (
                    <button
                      onClick={props.onOpenAssistant}
                      title="Chat with Company Files"
                      className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:border-violet-700 hover:text-violet-700 transition-colors"
                    >
                      <AutoAwesomeIcon style={{ fontSize: 18 }} />
                      <span>Chat with Company Files</span>
                    </button>
                  )}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search files and folders..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="block w-64 pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-violet-700 text-sm"
                    />
                    {searchQuery && (
                      <button
                        onClick={clearSearch}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <CloseIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>
                  {!isSearching && (
                    <CreateNewButton
                      currentFolder={currentFolder}
                      upload_file={upload_file}
                      upload_folder={upload_folder}
                      create_folder={create_folder}
                      companyId={props.homeCompany}
                      currentFolderName={documents[currentFolder]?.name}
                      currentFolderSearchable={
                        documents[currentFolder]?.assistantSearchable
                      }
                      onToggleSearchable={
                        canEditDocuments
                          ? toggle_folder_assistant_searchable
                          : null
                      }
                      onUrlImported={() => fetchDocuments(false)}
                      disabled={!canEditDocuments}
                      disabledTooltip="You need the documents editor permission to create or edit documents."
                    />
                  )}
                </div>
              </>
            )}
          </div>
          {isSearching && !showTrash && (
            <div className="flex items-center justify-between mt-2">
              <p className="text-sm text-gray-600">
                {items.folders.length + items.files.length} results found
              </p>
              <button
                onClick={clearSearch}
                className="text-sm text-violet-700 hover:text-violet-800"
              >
                Clear search
              </button>
            </div>
          )}
        </div>

        {!showTrash && (
          <div className="flex items-center justify-end px-6 pt-4 space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`w-9 h-9 flex items-center justify-center rounded-lg ${viewMode === 'grid' ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:bg-gray-100'}`}
              title="Grid view"
            >
              <AppsIcon style={{ fontSize: 20 }} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`w-9 h-9 flex items-center justify-center rounded-lg ${viewMode === 'list' ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:bg-gray-100'}`}
              title="List view"
            >
              <ViewListIcon style={{ fontSize: 20 }} />
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`w-9 h-9 flex items-center justify-center rounded-lg ${viewMode === 'preview' ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:bg-gray-100'}`}
              title="Preview"
            >
              <GridViewIcon style={{ fontSize: 20 }} />
            </button>
          </div>
        )}

        {showTrash ? (
          <TrashView
            homeCompany={props.homeCompany}
            homeStore={props.homeStore}
            onRestoreComplete={() => {
              fetchDocuments(false);
              fetchTrashCount();
            }}
            onTrashChanged={fetchTrashCount}
          />
        ) : (
          <div className="p-6 pb-20">
            {viewMode === 'list' && (items.folders.length > 0 || items.files.length > 0) && (
              <div className="flex items-center text-xs text-gray-400 uppercase tracking-wider px-4 py-2 border-b border-gray-200 mb-2">
                <button onClick={() => handleSortClick('name')} className={`flex items-center space-x-1 flex-1 hover:text-gray-600 ${sortBy === 'name' ? 'text-gray-600' : ''}`}>
                  <span>Name</span>
                  <SortArrow column="name" />
                </button>
                <button onClick={() => handleSortClick('size')} className={`flex items-center justify-end space-x-1 w-36 hover:text-gray-600 ${sortBy === 'size' ? 'text-gray-600' : ''}`}>
                  <span>Size</span>
                  <SortArrow column="size" />
                </button>
                <button onClick={() => handleSortClick('date')} className={`flex items-center justify-end space-x-1 w-32 hover:text-gray-600 ${sortBy === 'date' ? 'text-gray-600' : ''}`}>
                  <span>Date</span>
                  <SortArrow column="date" />
                </button>
                <button onClick={() => handleSortClick('type')} className={`flex items-center justify-end space-x-1 w-20 hover:text-gray-600 ${sortBy === 'type' ? 'text-gray-600' : ''}`}>
                  <span>Type</span>
                  <SortArrow column="type" />
                </button>
                <span className="w-6 h-6 ml-3 shrink-0"></span>
              </div>
            )}

            {items.folders.length > 0 && (
              <div className={viewMode === 'list' ? 'mb-2' : 'mb-8'}>
                {viewMode !== 'list' && (
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-700">
                      {isSearching ? 'Folders found' : 'Folders'}
                    </h2>
                    {isSearching && (
                      <span className="text-sm text-gray-500">
                        {items.folders.length} folder{items.folders.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
                <div className={viewMode === 'list' ? 'flex flex-col' : viewMode === 'preview' ? 'grid grid-cols-4 lg:grid-cols-2 md:grid-cols-1 gap-4' : 'grid grid-cols-3 lg:grid-cols-2 md:grid-cols-1 gap-4'}>
                  {items.folders.map((folder) => (
                    <FolderItem
                      key={folder.uuid}
                      folder={folder}
                      documents={documents}
                      handleFolderClick={handleFolderClick}
                      rename_folder={rename_folder}
                      delete_folder={requestDeleteFolder}
                      move_file={move_file}
                      move_folder={move_folder}
                      canEditDocuments={canEditDocuments}
                      viewMode={viewMode}
                      onExternalDrop={handleExternalDrop}
                      onToggleAssistantSearchable={canEditDocuments ? toggle_folder_assistant_searchable : null}
                      onSetPermissionLevel={canSetPermissions ? set_folder_permission_level : null}
                    />
                  ))}
                </div>
              </div>
            )}

            {items.files.length > 0 && (
              <div>
                {viewMode !== 'list' && (
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-700">
                      {isSearching ? 'Files found' : 'Files'}
                    </h2>
                    {isSearching && (
                      <span className="text-sm text-gray-500">
                        {items.files.length} file{items.files.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
                <div className={viewMode === 'list' ? 'flex flex-col' : viewMode === 'preview' ? 'grid grid-cols-4 lg:grid-cols-2 md:grid-cols-1 gap-4' : 'grid grid-cols-3 lg:grid-cols-2 md:grid-cols-1 gap-4'}>
                  {items.files.map((file, index) => {
                    // Check if parent folder is protected
                    const parentFolder = documents[file.parentUuid];
                    const isProtected = parentFolder?.isProtected || false;

                    return (
                      <FileItem
                        key={file.uuid}
                        file={file}
                        index={index}
                        handleFileClick={handleFileClick}
                        rename_file={rename_file}
                        delete_file={requestDeleteFile}
                        isFileDragging={isFileDragging}
                        setIsFileDragging={setIsFileDragging}
                        canEditDocuments={canEditDocuments}
                        isProtected={isProtected}
                        viewMode={viewMode}
                        onViewPhotos={setPhotosFile}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {isSearching && items.folders.length === 0 && items.files.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <SearchIcon className="h-12 w-12 text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg mb-2">No results found</p>
                <p className="text-gray-400 text-sm">
                  No files or folders match "{searchQuery}"
                </p>
              </div>
            )}

            {!isSearching && items.folders.length === 0 && items.files.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16">
                <p className="text-gray-400 text-lg">This folder is empty</p>
                {canEditDocuments && (
                  <p className="text-gray-400 text-sm mt-2">
                    Upload files or create a folder to get started
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {canEditDocuments && (showTrash ? (
          <button
            onClick={() => setShowTrash(false)}
            className="fixed bottom-6 right-6 flex items-center space-x-2 px-4 py-3 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full shadow-lg text-sm font-medium z-10"
          >
            <ChevronRightIcon style={{ fontSize: 18, transform: 'rotate(180deg)' }} />
            <span>Back to Company Files</span>
          </button>
        ) : trashCount > 0 && (
          <button
            onClick={() => setShowTrash(true)}
            className="fixed bottom-6 right-6 flex items-center space-x-2 px-4 py-3 bg-rose-100 text-rose-800 hover:bg-rose-200 rounded-full shadow-lg text-sm font-medium z-10"
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} />
            <span>Trash ({trashCount})</span>
          </button>
        ))}

        <ExtractedImagesModal
          open={Boolean(photosFile)}
          onClose={() => setPhotosFile(null)}
          companyId={props.homeCompany}
          file={photosFile}
        />
        <MarkdownPreviewModal
          open={Boolean(previewFile)}
          onClose={() => setPreviewFile(null)}
          companyId={props.homeCompany}
          file={previewFile}
        />
        <DeleteConfirmationModal
          open={deleteModalOpen}
          handleClose={() => {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }}
          handleConfirm={confirmDelete}
          itemName={deleteTarget?.name || ''}
          itemType={deleteTarget?.type || 'file'}
          hasContents={deleteTarget?.hasContents || false}
        />
      </div>
  );
}

export default DocumentsDisplay;
